import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import './ModerationDashboardPage.scss';

import {
  getModerationChannelsRequest,
  getModerationFlagsRequest,
  getModerationReportsCountersRequest,
  getModerationReportsRequest,
  getModerationUsersRequest,
  getTopChannelsRequest,
  getTopUsersRequest,
} from 'redux/reducers/moderation/reducer';

import {
  getModerationChannelsSelector,
  getModerationCountersSelector,
  getModerationFlagsSelector,
  getModerationReportsSelector,
  getModerationUsersSelector,
  getTopChannelsSelector,
  getTopUsersSelector,
} from 'redux/reducers/moderation/selectors';

import {
  CategoryQuickStats,
  CategoryStatItem,
} from 'components/Moderation/CategoryQuickStats/CategoryQuickStats';
import { FiltersValue } from 'components/Moderation/FiltersBar/FiltersBar';
import { KpiCardItem, KpiCards } from 'components/Moderation/KpiCards/KpiCards';
import {
  CaseRow,
  LastNewCasesTable,
} from 'components/Moderation/LastNewCasesTable/LastNewCasesTable';
import { TopRiskyChannels } from 'components/Moderation/TopRiskyChannels/TopRiskyChannels';

import { TopRiskyUsers } from 'components/Moderation/TopRiskyUsers/TopRiskyUsers';
import {
  Flag,
  GetChannelsResponse,
  GetFlagsResponse,
  ModerationChannel,
  ModerationChannelApi,
} from 'redux/reducers/moderation/types';
import { decodeRestrictionValue } from 'untils/decodeRestrictions';
import { transformFlagCounts } from 'untils/transformFlagCounts';

type MediaKind = 'video' | 'image' | 'gif' | 'audio' | 'document' | 'unknown';

const safeJsonParse = (s: any) => {
  if (typeof s !== 'string') return null;
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
};

const getExt = (name?: string) => {
  if (!name) return '';
  const m = name.toLowerCase().match(/\.([a-z0-9]+)$/);
  return m?.[1] ?? '';
};

const kindFromMime = (mime?: string): MediaKind => {
  if (!mime) return 'unknown';
  const m = mime.toLowerCase();
  if (m.startsWith('video/')) return 'video';
  if (m.startsWith('image/')) return m === 'image/gif' ? 'gif' : 'image';
  if (m.startsWith('audio/')) return 'audio';
  return 'document';
};

const kindFromExt = (ext: string): MediaKind => {
  if (!ext) return 'unknown';
  if (['mp4', 'mov', 'webm', 'mkv'].includes(ext)) return 'video';
  if (['jpg', 'jpeg', 'png', 'webp', 'bmp', 'heic'].includes(ext)) return 'image';
  if (['gif'].includes(ext)) return 'gif';
  if (['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(ext)) return 'audio';
  return 'document';
};

const extractMimeFromMessageData = (
  messageDataStr: any,
): { mimeType?: string; fileName?: string; kind?: MediaKind } => {
  // 1) швидкий regex по mime_type
  if (typeof messageDataStr === 'string') {
    const m = messageDataStr.match(/"mime_type"\s*:\s*"([^"]+)"/);
    if (m?.[1]) return { mimeType: m[1], kind: kindFromMime(m[1]) };
  }

  // 2) JSON parse
  const data = safeJsonParse(messageDataStr);
  const media = data?.media;

  // важливо: інколи тип видно по predicate_name медіа
  const mediaPred: string = String(media?.predicate_name ?? '').toLowerCase();
  // приклади: messageMediaPhoto, messageMediaDocument, messageMediaWebPage...
  const predHintsKind: MediaKind | undefined = mediaPred.includes('photo')
    ? 'image'
    : mediaPred.includes('gif')
    ? 'gif'
    : mediaPred.includes('video')
    ? 'video'
    : mediaPred.includes('audio')
    ? 'audio'
    : undefined;

  // 3) якщо є photo як обʼєкт — це фото
  if (media?.photo) {
    return { kind: 'image' };
  }

  // 4) document branch
  const doc = media?.document;
  const attrs: any[] = Array.isArray(doc?.attributes) ? doc.attributes : [];

  const mimeType =
    typeof doc?.mime_type === 'string' && doc.mime_type.trim() ? doc.mime_type : undefined;

  const fileNameAttr = attrs.find((a) => a?.predicate_name === 'documentAttributeFilename');
  const fileName =
    typeof fileNameAttr?.file_name === 'string' && fileNameAttr.file_name.trim()
      ? fileNameAttr.file_name
      : undefined;

  if (mimeType) return { mimeType, fileName, kind: kindFromMime(mimeType) };

  // 5) якщо mime нема — дивимось attributes
  const hasVideoAttr = attrs.some((a) => a?.predicate_name === 'documentAttributeVideo');
  if (hasVideoAttr) return { fileName, kind: 'video' };

  const hasAudioAttr = attrs.some((a) => a?.predicate_name === 'documentAttributeAudio');
  if (hasAudioAttr) return { fileName, kind: 'audio' };

  // 6) filename/ext
  const ext = getExt(fileName);
  const kindByExt = kindFromExt(ext);
  if (kindByExt !== 'unknown') return { fileName, kind: kindByExt };

  // 7) fallback по predicate_name
  if (predHintsKind) return { kind: predHintsKind };

  return {};
};

type Tab = 'active' | 'approve' | 'reject';
type FlagsState = GetFlagsResponse | Flag[] | null | undefined;

type QueueFilter = {
  contentType?: 'video' | 'image' | 'document';
  flag?: number;
  search?: string;
  date_from?: string;
  date_to?: string;
};

type FlagLike = {
  id: number;
  name?: string; // "IllegalNude"
  value?: number; // 2,4,8,32,128...
  description?: string; // "Nude content"
};

export const normalizeModerationChannel = (ch: ModerationChannelApi): ModerationChannel => {
  const participantsCount =
    ch.participantsCount ?? ch.participants_count ?? ch.participant_count ?? 0;

  const restricted = ch.restricted ?? ch.is_restricted ?? false;

  const restrictionReason = ch.restrictionReason ?? ch.restriction_reason;

  return {
    id: ch.id,
    title: ch.title ?? ch.name ?? `channel_${ch.id}`,
    about: ch.about ?? '',
    participantsCount,

    creator: ch.creator ?? ch.owner,

    restricted,
    restrictionReason,

    link: ch.link ?? ch.url,
    adult: ch.adult,
    verified: ch.verified,

    restrictions: ch.restrictions ?? [],
    violationsTotal: ch.violations_count,
  };
};

const toArray = <T,>(v: any): T[] => (Array.isArray(v) ? v : []);

const pickDataArray = <T,>(state: any): T[] => {
  if (!state) return [];
  if (Array.isArray(state)) return state as T[];
  if (Array.isArray(state?.data)) return state.data as T[];
  return [];
};

const buildRestrictionLabelMap = (flagsState: FlagsState) => {
  const map = new Map<number, string>();
  const arr = pickDataArray<FlagLike>(flagsState);

  for (const f of arr) {
    if (typeof f?.value !== 'number') continue;
    const label = (f.description || f.name || String(f.value)).trim();
    map.set(f.value, label);
  }

  return map;
};

const formatDateTime = (input?: string) => {
  if (!input) return '—';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return String(input);

  // 2025-12-29 11:15
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
};

const normalizeContentTypeLabel = (raw: any) => {
  const v = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (!v) return '—';

  // mime
  if (v.startsWith('video/')) return 'Video';
  if (v.startsWith('image/')) return v === 'image/gif' ? 'GIF' : 'Photo';
  if (v.startsWith('audio/')) return 'Audio';
  if (v.startsWith('application/')) return 'Document';

  // kind
  if (v === 'image' || v === 'photo' || v === 'picture') return 'Photo';
  if (v === 'gif') return 'GIF';
  if (v === 'video') return 'Video';
  if (v === 'audio') return 'Audio';
  if (v === 'document' || v === 'file') return 'Document';

  // filename/ext
  const ext = v.match(/\.([a-z0-9]+)$/)?.[1];
  if (ext) {
    if (['mp4', 'mov', 'webm', 'mkv'].includes(ext)) return 'Video';
    if (['jpg', 'jpeg', 'png', 'webp', 'bmp', 'heic'].includes(ext)) return 'Photo';
    if (['gif'].includes(ext)) return 'GIF';
    if (['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(ext)) return 'Audio';
    return 'Document';
  }

  return v[0].toUpperCase() + v.slice(1);
};

const pickChannelsArray = (resp: GetChannelsResponse | null): ModerationChannel[] => {
  if (!resp) return [];

  // найчастіші варіанти структури:
  const data: any = (resp as any).data;

  if (Array.isArray(data)) return data as ModerationChannel[];
  if (Array.isArray(data?.channels)) return data.channels as ModerationChannel[];
  if (Array.isArray((resp as any).channels)) return (resp as any).channels as ModerationChannel[];

  return [];
};

export const ModerationDashboardPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: Tab }>();

  // store
  const counters = useSelector(getModerationCountersSelector);
  const flags = useSelector(getModerationFlagsSelector);
  const reports = useSelector(getModerationReportsSelector);
  const users = useSelector(getModerationUsersSelector);
  const channelsResp = useSelector(getModerationChannelsSelector);
  const topChannels = useSelector(getTopChannelsSelector);
  const topUsers = useSelector(getTopUsersSelector);

  // local state
  const [page, setPage] = useState(1);
  const [perPage] = useState(39);

  const [filters, setFilters] = useState<FiltersValue>({
    dateFrom: undefined,
    dateTo: undefined,
    query: '',
    complaintType: '',
    contentType: '',
  });

  // restriction labels (value -> human)
  const restrictionLabelByCode = useMemo(() => buildRestrictionLabelMap(flags), [flags]);

  // flags list for selects
  const flagsList: FlagLike[] = useMemo(() => pickDataArray<FlagLike>(flags), [flags]);

  // queueFilter for backend
  const queueFilter: QueueFilter = useMemo(() => {
    const f: QueueFilter = {};
    if (filters.query) f.search = filters.query;
    if (filters.dateFrom) f.date_from = filters.dateFrom;
    if (filters.dateTo) f.date_to = filters.dateTo;

    if (filters.contentType) {
      if (filters.contentType === 'photo') f.contentType = 'image';
      else if (filters.contentType === 'video') f.contentType = 'video';
      else if (filters.contentType === 'document') f.contentType = 'document';
    }

    if (typeof filters.complaintType === 'number') {
      f.flag = filters.complaintType; // ✅ це restriction code
    }

    return f;
  }, [filters]);

  // initial loads
  useEffect(() => {
    dispatch(getModerationReportsCountersRequest());
    dispatch(getModerationFlagsRequest());
    dispatch(getModerationChannelsRequest({ current_page: 1, per_page: 10 }));
    dispatch(getTopChannelsRequest());
    dispatch(getTopUsersRequest());
    dispatch(
      getModerationUsersRequest({
        current_page: 1,
        per_page: 10,
        order_by: 'created_at',
        asc: false,
      }),
    );
  }, [dispatch]);

  // reports list for table
  useEffect(() => {
    let approved: boolean | null = null;
    let on_moderation: boolean | undefined = true;

    if (tab === 'approve') {
      approved = true;
      on_moderation = undefined;
    } else if (tab === 'reject') {
      approved = false;
      on_moderation = undefined;
    } else {
      approved = null;
      on_moderation = true;
    }

    dispatch(
      getModerationReportsRequest({
        current_page: page,
        per_page: perPage,
        ...queueFilter,
        approved,
        on_moderation,
        restricted: true,
      }),
    );
  }, [dispatch, tab, page, perPage, queueFilter]);

  // ===== KPI cards =====
  const kpis: KpiCardItem[] = useMemo(() => {
    const d = counters?.data;
    return [
      { key: 'total_violations', label: 'Total violations', value: d?.total ?? 0 },
      {
        key: 'violations_in_progress',
        label: 'Violations in progress',
        value: d?.in_progress ?? 0,
      },
      {
        key: 'violations_resolved_today',
        label: 'Violations resolved today',
        value: d?.resolved_today ?? 0,
      },

      { key: 'violations_resolved', label: 'Violations resolved', value: d?.resolved ?? 0 },
    ];
  }, [counters]);

  // ===== category quick stats =====
  const { categoryStats, totalReports } = useMemo(() => {
    const fc = counters?.data?.flag_counts;
    if (!fc) return { categoryStats: [] as CategoryStatItem[], totalReports: 0 };

    const transformed = transformFlagCounts(fc);
    const total = transformed.values.reduce((acc, v) => acc + v, 0);

    const items: CategoryStatItem[] = transformed.keys.map((key, idx) => {
      const count = transformed.values[idx] ?? 0;
      const restriction = Number(key);

      const label = restrictionLabelByCode.get(restriction) ?? `Restriction ${restriction}`;

      const percent = total > 0 ? count / total : 0;
      const severity = percent >= 0.3 ? 'high' : percent >= 0.15 ? 'medium' : 'low';

      return { label, count, restriction, severity };
    });

    items.sort((a, b) => b.count - a.count);

    return { categoryStats: items, totalReports: total };
  }, [counters, restrictionLabelByCode]);

  // ===== last new cases =====
  const lastNewCases: CaseRow[] = useMemo(() => {
    const arr = toArray<any>(reports?.data ?? []);

    console.log(reports?.data);

    return arr.map((r, idx) => {
      const report = r?.report ?? null;
      const channel = r?.channel ?? null;
      const user = r?.user ?? null;
      const message = r?.message ?? null;

      // ✅ caseId
      const caseIdRaw =
        report?.id ?? r?.caseId ?? r?.case_id ?? r?.id ?? r?.report_id ?? r?.reportId ?? null;

      const caseId = Number(caseIdRaw ?? 0);

      // ✅ restriction/category
      const restrictionCodeRaw = report?.restriction;

      const restrictionCode =
        typeof restrictionCodeRaw === 'number'
          ? restrictionCodeRaw
          : Number.isFinite(Number(restrictionCodeRaw))
          ? Number(restrictionCodeRaw)
          : null;

      const categoryLabel =
        r?.categoryLabel ??
        r?.category_label ??
        r?.flag_title ??
        (restrictionCode != null
          ? flags && decodeRestrictionValue(restrictionCode, flags.data)
          : undefined) ??
        '—';

      const messageDataStr = message?.message_data ?? '';
      const extracted = extractMimeFromMessageData(messageDataStr);

      const contentTypeRaw =
        r?.contentType ??
        r?.content_type ??
        r?.type_file ??
        extracted.mimeType ??
        extracted.kind ??
        extracted.fileName ??
        '';

      const contentTypeLabel = normalizeContentTypeLabel(contentTypeRaw);

      // ✅ channel
      const channelTitle = channel?.title ?? r?.channelTitle ?? r?.channel_title ?? '—';

      const channelLink =
        channel?.link ?? r?.channelLink ?? r?.channel_link ?? r?.url_channel ?? r?.link ?? '';

      // ✅ username
      const username =
        user?.username ?? r?.username ?? r?.owner_username ?? r?.owner_first_name ?? '';

      // ✅ createdAt
      const createdAt = formatDateTime(
        report?.created_at ?? report?.createdAt ?? message?.created_at ?? r?.created_at,
      );

      // ✅ reportsCnt
      const reportsCntRaw =
        r?.reportsCnt ?? r?.reports_cnt ?? r?.reportsCount ?? r?.cnt ?? r?.count ?? 1;

      const reportsCnt = Number(reportsCntRaw) || 1;

      const rowKey =
        caseIdRaw != null
          ? `report:${String(caseIdRaw)}`
          : `fallback:${String(restrictionCode ?? 'na')}:${String(
              report?.created_at ?? 'na',
            )}:${idx}`;

      return {
        rowKey,
        caseId,
        categoryLabel: String(categoryLabel),
        contentTypeLabel: contentTypeLabel || '—',
        channelTitle: String(channelTitle),
        channelLink: String(channelLink),
        username: String(username),
        createdAt: String(createdAt || '—'),
        reportsCnt,
        severity: (r?.severity as any) ?? 'medium',
      };
    });
  }, [reports, restrictionLabelByCode, flags]);

  const handleKpiClick = (key: string) => {
    // ✅ робимо логічний перехід у Queue
    if (key === 'new' || key === 'in_progress') navigate('/moderation/queue/active');
    if (key === 'resolved_today') navigate('/moderation/queue/approve');
    if (key === 'appeals') navigate('/moderation/reports');
  };

  const handleCategoryClick = (restriction?: number) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, complaintType: restriction ?? '' }));
    // лишаємось на дашборді, але ти можеш відправити в Queue:
    navigate('/moderation/queue/active');
  };

  const openCase = (caseId: number) => {
    navigate(`/moderation/detaile-queue/${caseId}`);
  };

  const openUser = (userId: number) => {
    navigate(`/moderation/users?userId=${userId}`);
  };

  const openChannel = (channelId: number) => {
    navigate(`/moderation/channels?channelId=${channelId}`);
  };

  return (
    <div className="md-dashboard-page">
      <div className="md-dashboard-page__top">
        <h1 className="md-dashboard-page__title">Moderation Dashboard</h1>
      </div>

      <div className="md-dashboard-page__kpis">
        <KpiCards items={kpis} onClick={handleKpiClick} />
      </div>

      <div className="md-dashboard-page__quick">
        <CategoryQuickStats
          total={totalReports}
          items={categoryStats}
          onChipClick={handleCategoryClick}
        />
      </div>

      {/* <div className="md-dashboard-page__quick">
        <UsersSegmentation counters={usersCounters} />
      </div> */}

      <div className="md-dashboard-page__grid">
        <div className="md-dashboard-page__left">
          <LastNewCasesTable items={lastNewCases} onOpenCase={openCase} />
        </div>

        <div className="md-dashboard-page__right">
          <TopRiskyChannels
            flags={flags}
            topChannels={topChannels?.data.channels || []}
            onOpenChannel={openChannel}
          />
          <div className="md-dashboard-page__spacer" />
          <TopRiskyUsers flags={flags} users={topUsers?.data?.users || []} onOpenUser={openUser} />
        </div>
      </div>

      <div className="md-dashboard-page__footer" />
    </div>
  );
};
